import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useCallback, useEffect, useState } from "react";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { TNextOfKinFormValues } from "../../../models/Onboarding/onboarding";
import { handleSubmitStage as handleResubmitStage3 } from "../../../services/onlineInterviewService";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { myProfileStageSubmit, saveEmergencyInfoThunk } from "../../../store/onboarding/thunk";
import { getCandidateData } from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { isSuperAdmin } from "../../../utils/roleHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleTick from "../../UI/Icons/CircleTick";
import RoundedTick from "../../UI/Icons/RoundedTick";
import TextInput from "../../UI/Inputs/TextInput";
const NextOfKin: FC = () => {
  const dispatch = useDispatch();
  const { isImpersonating } = useImpersonation();

  const [showMessage, setShowMessage] = useState<boolean>(false)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [pendingSteps, setPendingSteps] = useState<string>("")
  const user = useSelector((state) => state.auth.user);
    const {
      reopenedStageNumbers,
      reopenedSections,
      isStageReopenedFn,
      isSectionReopenedFn,
    } = useReopenedStages();
  const isStage3Reopen = isStageReopenedFn(3);

 

  const inProgress = useSelector(
    (state) => state.onboarding.inProgress
  );
  const profileCompletionStatus = useSelector((state) => state.onboarding.completedstatus)
  const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)
  const isStage3SectionReSubmitted = !!reviewCandidateData?.reopened_stages_status?.find((stage) => stage.stage_number === 3 && stage.status === "resubmitted");
  
  const isAdmin = isSuperAdmin(user);

  const emergencyInfo = useSelector(
    (state) =>
      isAdmin ? state.superadmin.reviewCandidateData?.emergency_info :
      state.onboarding.emergencyinfodetails
  );

  
  // const profileData = useSelector((state) => state.onboarding.personaldetails);
  const profileData = useSelector(
    (state) =>
      isAdmin ? state.superadmin.reviewCandidateData?.profile :
      state.onboarding.personaldetails
  );
  const verificationStatus = profileData?.verification_status;
  const currentStage = profileData?.current_stage ? profileData?.current_stage : null;

  const shouldFieldBeDisabled = () => {
    if (isImpersonating) return false;
    if(isAdmin) return false;
    return verificationStatus === 'verified';
  };

    const checkStageSubmittedOrNot = useCallback((stageNumber: number | null) => {
      if(currentStage === stageNumber){
        return true;
      } else{
        return false;
      } 
    }, [currentStage]);



  const handleSubmitStage = async(stageNumber: number) => {
    if(isAdmin) return;
    dispatch(showTransparentPreloaderAction())
    const payload = new FormData();
    payload.append("stage", "3");
    const myprofileStageResponse = await dispatch(myProfileStageSubmit(payload));
    dispatch(hideTransparentPreloaderAction());
    if (isFulfilled(myprofileStageResponse)) {
      dispatch(showNotificationAction('Stage 3 Submitted'));
      setTimeout(() => {
        globalThis.location.reload();
      }, NOTIFICATION_TIME_OUT);
    }
  }



  /** Submit the form for personal-details */
  const { handleSubmit, values, errors, touched, handleChange } = useFormik({
    validationSchema: createValidationSchema("nextOfKin"),
    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
    initialValues: {
      first_name: emergencyInfo?.first_name ?? "",
      last_name: emergencyInfo?.last_name ?? "",
      phone_number: emergencyInfo?.phone_number ?? "",
      relationship: emergencyInfo?.relationship ?? "",
    },
    onSubmit: async (values: TNextOfKinFormValues) => {
      const payload = { ...values };
      payload.candidate_uuid = isAdmin ? reviewCandidateData?.uuid || "" : "";
      const result = await dispatch(saveEmergencyInfoThunk(payload));
      if(isFulfilled(result)){
        await dispatch(getCandidateData({ candidate_id: reviewCandidateData?.uuid || "" }));
        (((profileData?.review_status === 'waiting' || profileData?.review_status === 'reopened') && profileData?.current_stage === 3) || isAdmin || isStage3Reopen) && setShowMessage(true);
        profileData?.verification_status !== "verified" && currentStage !== 3 && await handleSubmitStage(3);
      }
    },
  });

  useEffect(() => {
    if(values.first_name && values.last_name && values.phone_number && values.relationship){
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [values])

  useEffect(() => {
    if(profileCompletionStatus){
      const stage3SectionKeys = ["bankAccount", "casual_fair_work_statement", "contract_form", "emergency_info", "tfn_form", "superannuation_form"];
      const pendingStages = Object.keys(profileCompletionStatus).filter(stage => {
        const isStage3Section = stage3SectionKeys.includes(stage);
        const isIncomplete = !profileCompletionStatus[stage as keyof typeof profileCompletionStatus];
        const isReopened = isStage3Reopen ? reopenedSections.includes(stage) : true;
        return isStage3Section && isIncomplete && isReopened;
      });
      setPendingSteps(pendingStages.join(", "))
    }
  }, [profileCompletionStatus, reopenedSections, isStage3Reopen])

  const stage3Submitted = profileData?.current_stage === 3 && (profileData?.review_status === 'waiting' || profileData?.review_status === "reopened");


  

  return (
    <div className="d-flex flex-column gap-20">

      <div className="d-flex align-items-center gap-20 justify-content-between">
        <div className="fs-30 fw-400 text-black">Next of Kin</div>
        {
          isAdmin && (
            <CustomButton
              isLoading={inProgress}
              disabled={isDisabled}
              contentClass="d-flex"
              type="submit"
              variant="primary"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
            >
              <RoundedTick color={(isDisabled || inProgress) ? "#aaaaaa" : "#000000"} />
              {((profileData?.review_status === 'waiting' && profileData?.current_stage === 3) || profileData?.verification_status === "verified" || isStage3Reopen) ? "Save Changes" : isStage3Reopen ? "Save Changes" : "Submit Stage 3"}
            </CustomButton>
          )
        }
      </div>
      <div className="fs-16 fw-400 text-black">
        {
          isAdmin ? (
            <>Details for next of kin who should be contacted in the event of an emergency.</>
          ) : (
            <>
             Please provide details for your next of kin who should be contacted in
              the event of an emergency.
            </>
          )
        }
       
      </div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="flex-fill">
          <TextInput
            value={values.first_name}
            field={"first_name"}
            label={"First Name"}
            onChange={handleChange}
            touched={touched.first_name}
            error={errors.first_name as string}
            placeholder={" "}
            disabled={shouldFieldBeDisabled()}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.last_name}
            field={"last_name"}
            label={"Last Name"}
            onChange={handleChange}
            touched={touched.last_name}
            error={errors.last_name as string}
            placeholder={" "}
            disabled={shouldFieldBeDisabled()}
          />
        </div>
      </div>
      <div className="d-flex flex-column flex-lg-row gap-10">
        <div className="w-180px mobile-w-100">
          <TextInput
            value={values.phone_number}
            field={"phone_number"}
            label={"Phone Number"}
            onChange={handleChange}
            touched={touched.phone_number}
            error={errors.phone_number as string}
            placeholder={" "}
            disabled={shouldFieldBeDisabled()}
          />
        </div>
        <div className="flex-fill">
          <TextInput
            value={values.relationship}
            field={"relationship"}
            label={"Relationship"}
            onChange={handleChange}
            touched={touched.relationship}
            error={errors.relationship as string}
            placeholder={" "}
            disabled={shouldFieldBeDisabled()}
          />
        </div>
      </div>
      
        <div className="d-flex flex-column flex-lg-row gap-20 align-items-center justify-content-between">
          {
            ((!isAdmin && isStage3SectionReSubmitted) || (profileData?.verification_status !== "verified" && !isAdmin)) && (
              <CustomButton
                isLoading={inProgress}
                // disabled={isDisabled || checkStageSubmittedOrNot(3)}
                disabled={isDisabled}
                contentClass="d-flex"
                type="submit"
                variant="primary"
                className="btn-theme bg-teal text-black"
                onClick={handleSubmit}
              >
                <RoundedTick color={(isDisabled || inProgress) ? "#aaaaaa" : "#000000"} />
                {(stage3Submitted || profileData?.verification_status === "verified" || isStage3Reopen) ? "Save Changes" : isStage3Reopen ? "Save Changes" : "Submit Stage 3"}
              </CustomButton>
            )
          }          

          {
            isStage3Reopen && !isAdmin && (
              
                <CustomButton
                  type="submit"
                  contentClass="d-flex"
                  variant="primary"
                  disabled={pendingSteps !== ""}
                  onClick={() => handleResubmitStage3(dispatch, 3)}
                  className={`btn-theme bg-off-white text-black`}
                >
                  <CircleTick fill={pendingSteps !== "" ? "#aaaaaa" : "#000000"} />
                  Resubmit Stage 3
                </CustomButton>
                
             
            )
          }
        </div> 
      { showMessage && <div className="text-teal-dark">Next of kin is updated successfully!</div>}
    </div>
  );
};

export default NextOfKin;
